import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2, Paper, TextField, Toolbar, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  EntityForm,
  getEntityFormSchema,
} from "../../types/forms/entity/EntityForm";

interface AddEntityFormProps {
  onSubmit: (values: EntityForm) => Promise<void>;
}

export default function AddEntityForm({ onSubmit }: Readonly<AddEntityFormProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const validationSchema = getEntityFormSchema(t);

  const methods = useForm<EntityForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      acronym: "",
      name: "",
      fiscalNumber: "",
      website: "",
      notes: "",
    },
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("entities.table.add-entity")}
          </Typography>
        </Toolbar>
        
        <FormProvider {...methods}>
          <Box
            sx={{ p: 2 }}
            component="form"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 18, sm: 9 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={`${t("entities.entity.general.name")} *`}
                  {...methods.register("name")}
                  error={!!methods.formState.errors.name}
                  helperText={methods.formState.errors.name?.message}
                />
              </Grid2>
              <Grid2 size={{xs: 6, sm: 3}}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={`${t("entities.entity.general.acronym")} *`}
                  {...methods.register("acronym")}
                  error={!!methods.formState.errors.acronym}
                  helperText={methods.formState.errors.acronym?.message}
                />
              </Grid2>
              <Grid2 size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={`${t("entities.entity.general.fiscalNumber")} *`}
                  {...methods.register("fiscalNumber")}
                  error={!!methods.formState.errors.fiscalNumber}
                  helperText={methods.formState.errors.fiscalNumber?.message}
                />
              </Grid2>
              <Grid2 size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t("entities.entity.general.website")}
                  {...methods.register("website")}
                  error={!!methods.formState.errors.website}
                  helperText={methods.formState.errors.website?.message}
                />
              </Grid2>
              <Grid2 size={{xs: 12 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t("entities.entity.general.notes")}
                  placeholder={t("entities.entity.general.notes-placeholder")}
                  multiline
                  minRows={6}
                  {...methods.register("notes")}
                  error={!!methods.formState.errors.notes}
                  helperText={methods.formState.errors.notes?.message}
                />
              </Grid2>

              <Grid2 size={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3}}>
                <Box sx={{display: "flex", gap: 2}}>
                  <Button type="submit" variant="contained">
                    {t("generic.create")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                  >
                    {t("generic.cancel")}
                  </Button>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
        </FormProvider>
      </Paper>
    </Box>
  );
}
